import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, MenuItem, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  ClosureDTO,
  FeeArrangementDTO,
  getAllFeeArrangementsForClient,
  getCurrencyList,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  Label,
  WsButton,
  WsDatePicker,
  WsSelect,
  WsSelectObj,
  WsToggleButton,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';
import { number, object, string } from 'yup';

import { SelectWCreate } from '../../../components/SelectWCreate';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';

import AddFeeArrangementDialog from './AddFeeArrangementDialog';
export interface IComponentProps {
  formWizardSubMenuItemClick: (index: number) => void;
  setClosureDTO: (data: ClosureDTO) => void;
  currentClosureDTO: ClosureDTO;
  onCancel: () => void;
  setDirtyFieldsValue: (data: any) => void;
  getDirtyFieldsValue: any;
  placementType: string;
}

const validationSchema = object().shape({
  arrangementType: string().required('Please select Arrangement Type'),
  currencyId: string().required('Please select currency'),
  compensationAmount: number().required('Please enter compensation Amount'),
  period: string().required('Please select a period'),
});

const validationSchemaContract = object().shape({});

const ClosureCompensationDetails = ({
  formWizardSubMenuItemClick,
  setClosureDTO,
  currentClosureDTO,
  onCancel,
  setDirtyFieldsValue,
  getDirtyFieldsValue,
  placementType,
}: IComponentProps) => {
  const [currencyList, setCurrencyList] = useState<WsSelectObj[]>([]);
  const [feeArrangementList, setFeeArrangementList] = useState<FeeArrangementDTO[]>([]);
  const [feeArrangementOptions, setFeeArrangementOptions] = useState<WsSelectObj[]>([]);
  const [openFADialog, setOpenFADialog] = useState<boolean>(false);
  const [showOvertimeFields, setShowOvertimeFields] = useState<boolean>(false);

  const openCloseFADialog = (flag: boolean) => {
    setOpenFADialog(flag);
  };

  const methods = useForm<any>({
    defaultValues: {
      ...currentClosureDTO,
    },
    resolver: yupResolver(
      placementType === 'PERMANENT' ? validationSchema : validationSchemaContract
    ),
  });
  const {
    handleSubmit,
    register,
    formState: { dirtyFields },
  } = methods;
  const feeArrTypeVal = methods.watch('arrangementType');

  const calculatGauranteeDaysAndComissionValue = (
    feeArrangementId: string,
    selectedFeeArrangement?: FeeArrangementDTO,
    feeArrangementAmount?: number
  ) => {
    if (feeArrTypeVal === 'SELECTED' && feeArrangementId) {
      if (!selectedFeeArrangement) {
        selectedFeeArrangement = feeArrangementList.find((e) => e.value === feeArrangementId);
      }
      methods.setValue(
        'feeArrangementAmount',
        selectedFeeArrangement ? selectedFeeArrangement.fee : 0
      );
      const employmentGuarantee = selectedFeeArrangement?.employmentGuarantee;
      if (employmentGuarantee && selectedFeeArrangement) {
        const empGuaranteeDays =
          ({ DAYS: 1, WEEKS: 7, MONTHS: 30 }[selectedFeeArrangement?.empGuaranteePeriodUnit] ?? 0) *
          employmentGuarantee;
        methods.setValue('employmentGuaranteeDays', empGuaranteeDays);
        setGuaranteeEndDate(empGuaranteeDays);
      }
      if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'PERCENTAGE') {
        const commissionValue =
          (parseFloat(methods.getValues('compensationAmount')) * selectedFeeArrangement.fee) / 100;
        methods.setValue('commissionValue', commissionValue);
      } else {
        methods.setValue('commissionValue', selectedFeeArrangement?.fee ?? 0);
      }
    } else if (feeArrTypeVal === 'TEMP') {
      const feeType = methods.getValues('feeType');
      if (feeType === 'FIXED') {
        methods.setValue('commissionValue', feeArrangementAmount);
      } else if (feeType === 'PERCENTAGE') {
        const commissionValue =
          (parseFloat(methods.getValues('compensationAmount')) * (feeArrangementAmount as number)) /
          100;
        methods.setValue('commissionValue', commissionValue);
      }
    }
  };

  register('feeArrangementId', {
    onChange: (e) => {
      const feeArrangementId = e.target.value;
      calculatGauranteeDaysAndComissionValue(feeArrangementId);
    },
  });

  register('feeArrangementAmount', {
    onChange: (e) => {
      calculatGauranteeDaysAndComissionValue('', undefined, e.target.value as number);
    },
  });

  register('compensationAmount', {
    onChange: (e) => {
      const compensationAmount = e.target.value;
      const feeArrangementId = methods.getValues('feeArrangementId');
      if (feeArrangementId) {
        const selectedFeeArrangement: FeeArrangementDTO | undefined = feeArrangementList.find(
          (e) => e.value === feeArrangementId
        );
        if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'FIXED') {
          methods.setValue('commissionValue', selectedFeeArrangement.fee);
        } else if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'PERCENTAGE') {
          const commissionValue =
            (parseFloat(compensationAmount) * selectedFeeArrangement.fee) / 100;
          methods.setValue('commissionValue', commissionValue);
        }
      }
    },
  });

  const onSubmit = async (data: ClosureDTO) => {
    setDirtyFieldsValue({ ...getDirtyFieldsValue, ...dirtyFields });

    setClosureDTO(data);
    formWizardSubMenuItemClick(3);
  };

  const periodOptions = [
    {
      label: 'Hourly',
      value: 'HOURLY',
    },
    {
      label: 'Weekly',
      value: 'WEEKLY',
    },
    {
      label: 'Monthly',
      value: 'MONTHLY',
    },
    {
      label: 'Annually',
      value: 'ANNUALLY',
    },
    {
      label: 'Once',
      value: 'ONCE',
    },
  ];

  const getFeeArrangementData = async () => {
    const response = await getAllFeeArrangementsForClient(currentClosureDTO.clientId);
    setFeeArrangementList(response.data);
    setFeeArrangementOptions(
      response.data.map((fa: FeeArrangementDTO) => ({
        label: fa.feeArrangementLevel + ' - ' + fa.fee + (fa.feeType === 'PERCENTAGE' ? '%' : ''),
        value: fa?.value,
      }))
    );
  };

  useEffect(() => {
    const getCurrencyData = async () => {
      const response = await getCurrencyList();
      setCurrencyList(
        response.data.map((currency) => ({
          label: currency?.symbol,
          value: currency?.id,
        }))
      );
    };
    getCurrencyData();
    const getFeeArrangements = async () => {
      const response = await getAllFeeArrangementsForClient(currentClosureDTO.clientId);
      setFeeArrangementList(response.data);
      setFeeArrangementOptions(
        response.data.map((fa: FeeArrangementDTO) => ({
          label: fa.feeArrangementLevel + ' - ' + fa.fee + (fa.feeType === 'PERCENTAGE' ? '%' : ''),
          value: fa?.value,
        }))
      );
    };
    getFeeArrangements();
  }, [currentClosureDTO]);

  const setGuaranteeEndDate = (days: number) => {
    if (currentClosureDTO.joiningDate) {
      const date = new Date(currentClosureDTO.joiningDate);
      date.setDate(date.getDate() + days);
      methods.setValue('employmentGuaranteeEndDate', date);
    }
  };

  const setFeeArrangement = (data: FeeArrangementDTO) => {
    getFeeArrangementData();
    if (data.id) {
      methods.setValue('feeArrangementId', data.id);
      calculatGauranteeDaysAndComissionValue(data.id, data);
    }
  };

  const feeArrTypes = [
    {
      label: 'Add Manually',
      value: 'TEMP',
    },
    {
      label: 'Select Arrangement',
      value: 'SELECTED',
    },
  ];

  const feeTypes = [
    {
      label: 'Percentage',
      value: 'PERCENTAGE',
    },
    {
      label: 'Fixed',
      value: 'FIXED',
    },
  ];

  const onOvertimeCheck = (checked: boolean) => {
    checked ? setShowOvertimeFields(true) : setShowOvertimeFields(false);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            {placementType === 'PERMANENT' && (
              <Grid container py={1}>
                <Grid item p={1} sm={4}>
                  <WsSelect
                    label={'Currency'}
                    name="currencyId"
                    selectArray={currencyList}
                    size="small"
                    showMandatorySymbol
                  />
                </Grid>
                <Grid item p={1} sm={4}>
                  <ControlledTextInput
                    name="compensationAmount"
                    type="text"
                    fullWidth
                    labelText="Compensation Amount"
                    variant="outlined"
                    showMandatorySymbol
                  />
                </Grid>
                <Grid item p={1} sm={3.5}>
                  <WsSelect
                    label="Period"
                    name="period"
                    selectArray={periodOptions}
                    size="small"
                    showMandatorySymbol
                  />
                </Grid>
                <Grid item p={1} sm={3.75}>
                  <Label>Fee Arrangement Type</Label>
                  <Controller
                    control={methods.control}
                    name="arrangementType"
                    render={({ field: { onChange, value } }) => (
                      <WsToggleButton
                        buttonGroupPadding={6}
                        buttonsList={feeArrTypes}
                        defaultValue={value ?? 'SELECTED'}
                        selectedIndexValue={onChange}
                      />
                    )}
                  />
                </Grid>
                {feeArrTypeVal === 'TEMP' && (
                  <Grid item p={1} sm={3}>
                    <Label>Service Fees</Label>
                    <Controller
                      control={methods.control}
                      name="feeType"
                      render={({ field: { onChange, value } }) => (
                        <WsToggleButton
                          buttonGroupPadding={6}
                          buttonsList={feeTypes}
                          defaultValue={value ?? 'FIXED'}
                          selectedIndexValue={onChange}
                        />
                      )}
                    />
                  </Grid>
                )}
                {feeArrTypeVal === 'SELECTED' && (
                  <Grid item p={1} sm={2.75}>
                    <SelectWCreate
                      onCreate={() => {
                        openCloseFADialog(true);
                      }}
                      name="feeArrangementId"
                      defaultValue={currentClosureDTO?.feeArrangementId ?? ''}
                      label={'Fee Arrangement'}
                      showMandatorySymbol={true}
                    >
                      {feeArrangementOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </SelectWCreate>
                  </Grid>
                )}
                <Grid item p={1} sm={feeArrTypeVal === 'SELECTED' ? 5 : 4.75}>
                  <ControlledTextInput
                    name="feeArrangementAmount"
                    type="text"
                    fullWidth
                    labelText="Arrangement Amount / Percentage"
                    variant="outlined"
                    disabled={feeArrTypeVal === 'SELECTED'}
                  />
                </Grid>
                <Grid item p={1} sm={5.75}>
                  <ControlledTextInput
                    name="commissionValue"
                    type="text"
                    fullWidth
                    labelText="Commission Value"
                    variant="outlined"
                    disabled
                  />
                </Grid>
                <Grid item p={1} sm={5.75}>
                  <ControlledTextInput
                    name="employmentGuaranteeDays"
                    type="text"
                    fullWidth
                    labelText="Employment Guarantee Days"
                    variant="outlined"
                    onBlur={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setGuaranteeEndDate(parseInt((event.target as HTMLInputElement).value))
                    }
                  />
                </Grid>
                <Grid item p={1} sm={5.75}>
                  <Label>Employment Guarantee End Date</Label>
                  <WsDatePicker name="employmentGuaranteeEndDate" label="" />
                </Grid>
              </Grid>
            )}
            {placementType === 'CONTRACT' && (
              <Grid container py={1}>
                <Grid item p={1} sm={11.5}>
                  <WsTypography
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.SECONDARY}
                    bold={true}
                  >
                    Buy Details
                  </WsTypography>
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <WsSelect
                    label="Currency"
                    name="buyRateCurrencyId"
                    selectArray={currencyList}
                    size="small"
                  />
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <ControlledTextInput
                    name="buyRateAmount"
                    type="text"
                    fullWidth
                    labelText="Rate"
                    variant="outlined"
                  />
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <WsSelect
                    label="Period"
                    name="buyRateFrequency"
                    selectArray={periodOptions}
                    size="small"
                  />
                </Grid>
                <Grid item p={1} sm={11.5}>
                  <ControlledTextArea
                    name="buyRateDescription"
                    defaultValue={''}
                    minRows={4}
                    placeholder="Comments/Description"
                  />
                </Grid>
                <Grid item p={1} sm={11.5}>
                  <WsTypography
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.SECONDARY}
                    bold={true}
                  >
                    Sell Details
                  </WsTypography>
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <WsSelect
                    label="Currency"
                    name="sellRateCurrencyId"
                    selectArray={currencyList}
                    size="small"
                  />
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <ControlledTextInput
                    name="sellRateAmount"
                    type="text"
                    fullWidth
                    labelText="Rate"
                    variant="outlined"
                  />
                </Grid>
                <Grid item p={1} sm={3.83}>
                  <WsSelect
                    label="Period"
                    name="sellRateFrequency"
                    selectArray={periodOptions}
                    size="small"
                  />
                </Grid>
                <Grid item p={1} sm={11.5}>
                  <ControlledTextArea
                    name="sellRateDescription"
                    defaultValue={''}
                    minRows={4}
                    placeholder="Comments/Description"
                  />
                </Grid>
                <Grid item p={1} sm={11.5}>
                  <WsCheckBox
                    label="Overtime Applicable"
                    name="overtimeApplicable"
                    bold={true}
                    onClick={onOvertimeCheck}
                  />
                </Grid>
                {showOvertimeFields && (
                  <>
                    <Grid item p={1} sm={11.5}>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        color={WsTypoColors.SECONDARY}
                        bold={true}
                      >
                        Buy Details
                      </WsTypography>
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <WsSelect
                        label="Currency"
                        name="overTimeBuyRateCurrencyId"
                        selectArray={currencyList}
                        size="small"
                      />
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <ControlledTextInput
                        name="overTimeBuyRateAmount"
                        type="text"
                        fullWidth
                        labelText="Rate"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <WsSelect
                        label="Period"
                        name="overTimeBuyRateFrequency"
                        selectArray={periodOptions}
                        size="small"
                      />
                    </Grid>
                    <Grid item p={1} sm={11.5}>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        color={WsTypoColors.SECONDARY}
                        bold={true}
                      >
                        Sell Details
                      </WsTypography>
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <WsSelect
                        label="Currency"
                        name="overTimeSellRateCurrencyId"
                        selectArray={currencyList}
                        size="small"
                      />
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <ControlledTextInput
                        name="overTimeSellRateAmount"
                        type="text"
                        fullWidth
                        labelText="Rate"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item p={1} sm={3.83}>
                      <WsSelect
                        label="Period"
                        name="overTimeSellRateFrequency"
                        selectArray={periodOptions}
                        size="small"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
              {`Cancel`}
            </WsButton>
            <WsButton variant="contained" size="medium" type="submit">
              {`Next`}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
      {openFADialog && (
        <AddFeeArrangementDialog
          clientId={currentClosureDTO.clientId}
          openDialog={openFADialog}
          handleClose={() => openCloseFADialog(false)}
          onSave={(data) => setFeeArrangement(data)}
        />
      )}
    </>
  );
};

export default ClosureCompensationDetails;
